<template>
	<div>
		<Flyout :title="title" :display="display" @show="close">
			<template #flyout-content>
				<div :class="['integrations-info', loading && 'is-disabled']">
					<div class="integrations-info__name">
						<Input
							ariaLabel="Aria Label"
							className="field__input"
							label="Name"
							name="dcsIntegration"
							:disabled="!isNew"
							:required="true"
							v-model="dataSource.dcsIntegration"
							:forceCustomErrorMessage="
								dcsIntegrationCustomErrorMessage
							"
							@input="setDcsIntegration"
						/>
					</div>
					<div class="integrations-info__partyid">
						<Input
							ariaLabel="Aria Label"
							className="field__input"
							label="Party ID"
							name="partyId"
							:required="true"
							v-model="dataSource.partyId"
							:forceCustomErrorMessage="partyIdErrorMessage"
							@input="setPartyId"
						/>
					</div>
					<div class="integrations-info__username">
						<Input
							ariaLabel="Aria Label"
							className="field__input"
							label="Username"
							name="username"
							:required="true"
							v-model="dataSource.username"
							:forceCustomErrorMessage="usernameErrorMessage"
							@input="setUsername"
						/>
					</div>
					<div class="integrations-info__password">
						<Input
							ariaLabel="Aria Label"
							className="field__input"
							label="Password"
							name="password"
							:required="true"
							v-model="dataSource.password"
							:forceCustomErrorMessage="passwordErrorMessage"
							@input="setPassword"
						/>
					</div>
					<div class="integrations-info__dateactive">
						<Input
							ariaLabel="Aria Label"
							className="field__input"
							name="'dateActive'"
							:disabled="true"
							:label="
								(dataSource.active ? 'Active' : 'Inactive') +
									'Date'
							"
							:value="parseToLocaleString(dataSource.dateActive)"
						/>
					</div>
					<div class="integrations-info__active">
						<Toggle
							:disabled="isNew"
							:label="dataSource.active ? 'Active' : 'Inactive'"
							v-model="dataSource.active"
						/>
					</div>
				</div>
				<Spinner
					class="spinner"
					v-if="loading"
					:processor="{ loading: true }"
				/>
			</template>
			<template #flyout-footer>
				<ButtonGroup
					:items="[
						{
							btnClick: saveAction,
							className: 'btn--primary',
							labelText: isNew ? 'Create' : 'Update',
							disabled: disableSaveButton
						},
						{
							btnClick: close,
							className: 'btn btn--link',
							labelText: 'Cancel'
						}
					]"
				/>
			</template>
		</Flyout>
	</div>
</template>

<script>
import ButtonGroup from '@/components/forms/button/button-group';
import Spinner from '@/components/visual/spinner/spinner';
import Input from '@/components/forms/input/input';
import Flyout from '@/components/shells/flyout/flyout';
import Toggle from '@/components/forms/selection-control/toggle/toggle';
import ValidatePermissions from '@/sections/integration/validate-permission-mixins';
import { toLocaleString } from '@/utils/date/toLocaleString';
import { generateRandomString } from '@/utils/function';
import { required } from 'vuelidate/lib/validators';
import { deepEqual } from '@/utils/object';

//constants
import {
	SAVE_THIRD_PARTY,
	SET_LAST_THIRD_PARTYID,
	TOAST_MESSAGES_PUSH
} from '@/sections/integration/integrations-mutation-types';

const storeModule = 'integrations';

export default {
	name: 'ThirdPartySettings',
	mixins: [ValidatePermissions],
	components: {
		ButtonGroup,
		Input,
		Flyout,
		Toggle,
		Spinner
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		display: {
			type: Boolean,
			require: true
		},
		thirdparties: {
			type: Object,
			default: () => {}
		},
		operation: {
			type: String,
			default: 'edit',
			validation: value => ['edit', 'create'].includes(value)
		},
		source: {
			type: Object,
			default: () => {}
		}
	},
	validations: {
		dataSource: {
			dcsIntegration: {
				required
			},
			partyId: {
				required
			},
			username: {
				required
			},
			password: {
				required
			}
		}
	},
	data() {
		return {
			dataSource: {},
			fieldHasErrors: false,
			confirmartionTitle: 'ThirdParty Settings',
			loading: false
		};
	},
	computed: {
		dcsIntegrationCustomErrorMessage() {
			if (this.$v.dataSource.dcsIntegration.$dirty) {
				if (!this.$v.dataSource.dcsIntegration.required) {
					return 'ThirdParty Name is required';
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		partyIdErrorMessage() {
			if (this.$v.dataSource.partyId.$dirty) {
				if (!this.$v.dataSource.partyId.required) {
					return 'Party Id is required';
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		usernameErrorMessage() {
			if (this.$v.dataSource.username.$dirty) {
				if (!this.$v.dataSource.username.required) {
					return 'Username is required';
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		passwordErrorMessage() {
			if (this.$v.dataSource.password.$dirty) {
				if (!this.$v.dataSource.password.required) {
					return 'Password is required';
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		disableSaveButton() {
			return (
				this.$v.$invalid || this.fieldHasErrors || !this.pendingChanges
			);
		},
		isNew() {
			return !this.source.id ? true : false;
		},
		title() {
			return `${!this.source.id ? 'New' : 'Edit'} ThirdParty`;
		},
		pendingChanges() {
			return !deepEqual(this.dataSource, this.source || {});
		}
	},
	methods: {
		setDcsIntegration(value) {
			this.dataSource.dcsIntegration = value;
			this.$v.dataSource.dcsIntegration.$touch();
		},
		setPartyId(value) {
			this.dataSource.partyId = value;
			this.$v.dataSource.partyId.$touch();
		},
		setUsername(value) {
			this.dataSource.username = value;
			this.$v.dataSource.username.$touch();
		},
		setPassword(value) {
			this.dataSource.password = value;
			this.$v.dataSource.password.$touch();
		},
		close() {
			this.$emit('show', false);
		},
		async saveAction() {
			//validate before call api
			const { thirdPartyId, partyId, dcsIntegration } = this.dataSource;
			const matches = (this.thirdparties.items || []).find(
				x =>
					x.partyId === partyId && x.dcsIntegration === dcsIntegration
			);
			if (matches) {
				//in the add action validate if it exists,
				//in the update action validate if there are duplicates
				if (
					this.isNew ||
					(!this.isNew && matches.thirdPartyId !== thirdPartyId)
				) {
					this.$store.dispatch(TOAST_MESSAGES_PUSH, {
						statusCode: 400,
						message: `${dcsIntegration} already exists.`
					});
					this.fieldHasErrors = true;
					return;
				}
			}

			let payload = { ...this.dataSource };
			const date = new Date(payload.dateActive);
			payload.dateActive = this.parseToLocaleString(date);

			this.loading = true;

			await this.$store.dispatch(
				`${storeModule}/${SAVE_THIRD_PARTY}`,
				payload
			);
			this.loading = false;
			this.close();
			this.$emit('save');
		},
		createThirdParty() {
			const self = this;
			this.dataSource = {};
			this.$store
				.dispatch(`${storeModule}/${SET_LAST_THIRD_PARTYID}`)
				.then(resp => {
					self.parseReponse(resp);
				});
		},
		parseReponse(data) {
			let newPartyId;
			if (data) {
				try {
					const text = data.replace(/[0-9]/g, '');
					const value = data.replace(/[^0-9.]/g, '');
					const parsed = Number.parseInt(value);
					if (!Number.isNaN(parsed)) {
						newPartyId = `${text}${parsed + 1}`;
					}
				} catch {
					newPartyId = '';
				}
			}

			this.dataSource = {
				active: true,
				dateActive: Date.now(),
				dcsParty: 'SECUREVUE',
				dcsIntegration: '',
				partyId: newPartyId,
				password: generateRandomString('all', 25),
				username: generateRandomString('', 20)
			};
		},
		parseToLocaleString(value) {
			if (value) {
				return toLocaleString(value, {
					month: 'numeric', //2-digit
					day: 'numeric',
					year: 'numeric'
				});
			}
			return '';
		}
	},
	watch: {
		display: {
			immediate: true,
			handler(val) {
				if (val) {
					if (this.isAuthorized(this.source || {})) {
						this.dataSource = { ...this.source };
						if (this.isNew) {
							this.createThirdParty();
						}
					}
				}
			}
		}
	}
};
</script>

<style lang="scss">
.integrations-info {
	> * {
		font-size: $flyout-content-font-size;
		min-width: 0;
	}

	display: grid;
	gap: $gutter-lg;
	margin-bottom: $gutter-lg;
}
.spinner .spinner--diamond {
	position: inherit;
}

/*------------------------------------*\
  #BASE MOBILE LANDSCAPE-UP
\*------------------------------------*/
@include mq($from: sm-l) {
	.integrations-info {
		grid-template-columns: 1fr 1fr;
	}
}

/*------------------------------------*\
  #BASE DESKTOP-UP
\*------------------------------------*/
@include mq($from: lg) {
	.integrations-info {
		grid-template-columns: 1fr 1fr 1fr 1fr;
	}
}
</style>
